(() => {
  if (document.documentElement.lang.split('-')[0] !== 'ar' || window.bazRefinedReviewsLoaded) return;
  const config = document.getElementById('BazRefinedReviewTranslations');
  if (!config) return;

  let dictionary;
  try {
    dictionary = JSON.parse(config.textContent);
  } catch {
    return;
  }
  window.bazRefinedReviewsLoaded = true;

  const normalize = (value) => value.replace(/[\u2018\u2019]/g, "'").replace(/\s+/g, ' ').trim().toLowerCase();
  const translations = new Map(Object.entries(dictionary).map(([source, target]) => [normalize(source), target]));
  const modalSelector = '.jdgm-write-review-modal, [role="dialog"][class*="jdgm-write-review"], [class*="jdgm-write-review-modal__content"]';
  const rootSelector = '.jdgm-widget, .jm-review-widget, ' + modalSelector;
  const labelSelector = [
    '.jm-review-widget-minimal-header__title', '.jm-review-widget-header__title',
    '.jm-review-widget-header__subtitle', '.jm-no-reviews-state__text',
    '.jm-action-buttons__button', '.jm-text', '.jdgm-rev-widg__title',
    '.jdgm-write-rev-link', '.jdgm-sort-dropdown option',
    '.jm-sort-dropdown option', '.jdgm-write-review-modal__title',
    '.jdgm-write-review-modal__intro', '.jdgm-write-review-modal__field-label',
    '.jdgm-write-review-modal__field-help', '.jdgm-write-review-modal__anonymous-help',
    '.jdgm-write-review-modal__checkbox-text', '.jdgm-write-review-modal__nav-btn-next',
    '.jdgm-write-review-modal__nav-btn-previous', '.jdgm-write-review-modal__nav-btn-back',
    '.jdgm-write-review-modal__nav-btn-submit', '.jdgm-write-review-modal__submit-btn',
    '.jdgm-write-review-modal__error', '.jdgm-write-review-modal__field-error',
    '.jdgm-write-review-modal__upload-label', '.jdgm-write-review-modal__upload-text',
    '.jdgm-write-review-modal__success-title', '.jdgm-write-review-modal__success-text',
    '.jdgm-write-review-modal__skip-btn', '.jdgm-write-review-modal__nav-btn',
    '.jdgm-form__title', '.jdgm-form__name-fieldset label',
    '.jdgm-form__email-fieldset label', '.jdgm-form__rating-fieldset label',
    '.jdgm-form__title-fieldset label', '.jdgm-form__body-fieldset label',
    '.jdgm-submit-rev', '.jdgm-cancel-rev'
  ].join(',');
  const protectedSelector = [
    '.jdgm-rev', '.jm-review-card', '.jm-review-item', '.jm-review-list-item',
    '[class*="review-body"]', '[class*="review-content"]',
    '[class*="reviewer-name"]', '[class*="review-text"]',
    '[class*="review-title"]', '[class*="review-card"]',
    '[contenteditable="true"]', 'script', 'style', 'textarea'
  ].join(',');
  const observers = new Map();
  const queuedRoots = new Set();
  const timers = new Set();
  const controller = new AbortController();
  let frame = 0;
  let discoveryCount = 0;

  function translated(value) {
    const target = translations.get(normalize(value));
    if (!target || target === value || /^translation missing:/i.test(target)) return null;
    return target;
  }

  function translateNode(node) {
    const parent = node.parentElement;
    if (!parent || parent.closest(protectedSelector)) return;
    const target = translated(node.nodeValue);
    if (!target) return;
    const leading = node.nodeValue.match(/^\s*/)[0];
    const trailing = node.nodeValue.match(/\s*$/)[0];
    node.nodeValue = leading + target + trailing;
  }

  function translateElement(element) {
    if (element.closest(protectedSelector)) return;
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode())) translateNode(node);
  }

  function translateRoot(root) {
    if (!root.isConnected) return;
    for (const element of root.querySelectorAll(labelSelector)) translateElement(element);
    if (root.matches(labelSelector)) translateElement(root);
    // Rating endpoint text is form interface copy, scoped to its rating group.
    // Do not scan other modal text such as customer-entered review previews.
    if (root.matches(modalSelector) || root.closest(modalSelector)) {
      const ratingName = translations.get(normalize('Rating'));
      for (const group of root.querySelectorAll('[role="group"][aria-label], fieldset')) {
        const name = group.getAttribute('aria-label') || group.querySelector('legend')?.textContent || '';
        if (normalize(name) !== 'rating' && (!ratingName || normalize(name) !== normalize(ratingName))) continue;
        translateElement(group);
      }
    }
    // A dialog may carry its accessible name on the discovered root itself.
    for (const element of [root, ...root.querySelectorAll('[placeholder], [aria-label], [title]')]) {
      if (element.closest(protectedSelector) && !element.matches('textarea')) continue;
      for (const attribute of ['placeholder', 'aria-label', 'title']) {
        if (!element.hasAttribute(attribute)) continue;
        const target = translated(element.getAttribute(attribute));
        if (target) element.setAttribute(attribute, target);
      }
    }
  }

  function schedule(root) {
    queuedRoots.add(root);
    if (frame) return;
    frame = requestAnimationFrame(() => {
      frame = 0;
      for (const current of queuedRoots) translateRoot(current);
      queuedRoots.clear();
    });
  }

  function discoverRoots() {
    for (const [root, observer] of observers) {
      if (root.isConnected) continue;
      observer.disconnect();
      observers.delete(root);
    }
    for (const root of document.querySelectorAll(rootSelector)) {
      if (observers.has(root)) {
        schedule(root);
        continue;
      }
      const observer = new MutationObserver(() => schedule(root));
      observer.observe(root, {
        childList: true, subtree: true, characterData: true,
        attributes: true, attributeFilter: ['placeholder', 'aria-label', 'title']
      });
      observers.set(root, observer);
      schedule(root);
    }
  }

  function after(delay, callback) {
    const timer = setTimeout(() => {
      timers.delete(timer);
      callback();
    }, delay);
    timers.add(timer);
  }

  function bootstrap() {
    discoverRoots();
    discoveryCount += 1;
    if (discoveryCount < 12) after(1000, bootstrap);
  }

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest(rootSelector + ', [class*="jdgm-write-review-modal"]')) return;
    discoverRoots();
    for (const delay of [50, 200, 500, 1000, 2000]) after(delay, discoverRoots);
  }, { signal: controller.signal, passive: true });
  document.addEventListener('shopify:section:load', discoverRoots, { signal: controller.signal });
  window.addEventListener('pagehide', (event) => {
    if (event.persisted) return;
    controller.abort();
    for (const observer of observers.values()) observer.disconnect();
    for (const timer of timers) clearTimeout(timer);
    if (frame) cancelAnimationFrame(frame);
  }, { once: true });
  bootstrap();
})();
